
(() => {
  "use strict";

  if (window.__TDOG_THEME_CUSTOMIZER_LOADED__) {
    return;
  }
  window.__TDOG_THEME_CUSTOMIZER_LOADED__ = true;

  /************************************************************
   * Configuration
   ************************************************************/
  const CONFIG = Object.freeze({
    styleId: "tdog-style-injected",
    logPrefix: "[TDog Script]",
    debugStorageKey: "tdogThemeDebug",
    debugQueryParam: "tdogDebug",
    globalObserverDebounceMs: 75,
    rapiSyncDebounceMs: 60,
    engraveSyncDebounceMs: 60,
    waitForRapiTimeoutMs: 10000,
    waitForRapiPollMs: 250
  });

  const SELECTORS = Object.freeze({
    personaliseButton: "div.cl-po--swatch.cl-po--swatch-button.cl-po--small",
    productCard: ".product-card",
    productCardMedia: ".product-card__media",
    newBadgeConfig: 'script[type="application/json"][id^="new-badge-config-"]',
    rapiRoot: ".rapi-bundles, [class*='rapi-bundle-content']",
    productFormWrapper: ".product-form-wrapper",
    productForm: 'form[is="product-form"], form[action="/cart/add"]',
    buyButtonByFormId: (formId) => `button.product-form__submit[form="${cssEscape(formId)}"]`,
    buyPriceContainerByFormId: (formId) => `#BuyButtonPrice-${cssEscape(formId)}`,
    buyButtonLabel: ".btn-text > span:first-child",
    checkedBundleRadio: ".rapi-bundles__bar input[type='radio']:checked",
    bundleVariantSelect: ".rapi-bundles__bar-variant-select",
    bundleTitle: ".rapi-bundles__bar-title",
    bundlePrice: ".rapi-bundles__bar-price",
    engraveWrapper: ".cl-po--wrapper",
    engraveGroup: '.cl-po--option[data-option][data-option$="Group"]',
    engraveCollapse: ".cl-po--collapse",
    engravePanel: ".cl-po--panel",
    engraveTextInput: 'input.cl-po--input[type="text"], textarea.cl-po--input, input[type="text"], textarea',
    engraveFontAny: '.cl-po--option[data-option$="Font"] input[type="radio"]',
    engraveFontSelected: '.cl-po--option[data-option$="Font"] input[type="radio"]:checked',
    engraveApproveCheckbox: '.cl-po--option[data-option^="Approve"] input[type="checkbox"]',
    formSubmitter: "button, input[type='submit'], input[type='image']"
  });

  /************************************************************
   * Debug logging
   ************************************************************/
  function isDebugEnabled() {
    try {
      const query = new URLSearchParams(window.location.search);
      const queryValue = query.get(CONFIG.debugQueryParam);
      if (queryValue === "1" || queryValue === "true") return true;
      if (queryValue === "0" || queryValue === "false") return false;

      const storageValue = window.localStorage?.getItem(CONFIG.debugStorageKey);
      if (storageValue === "1" || storageValue === "true") return true;
      if (storageValue === "0" || storageValue === "false") return false;

      return document.documentElement?.dataset?.tdogDebug === "1";
    } catch {
      return false;
    }
  }

  const DEBUG = isDebugEnabled();

  function log(...args) {
    if (!DEBUG) return;
    console.log(CONFIG.logPrefix, ...args);
  }

  function warn(...args) {
    if (!DEBUG) return;
    console.warn(CONFIG.logPrefix, ...args);
  }

  function error(...args) {
    console.error(CONFIG.logPrefix, ...args);
  }

  function safeRun(name, fn, fallbackValue) {
    try {
      return fn();
    } catch (err) {
      warn(`${name} failed`, err);
      return fallbackValue;
    }
  }

  /************************************************************
   * CSS
   ************************************************************/
  const CSS = `
    .tdog-personalise{border-radius:9999px;padding:16px 28px;min-height:56px;font-size:16px;font-weight:600;line-height:1.4;color:#fff;display:inline-flex;align-items:center;justify-content:center;background-color:#de6ac1;box-shadow:0 1px 3px rgba(0,0,0,.2);border:none;width:100%;transition:filter .2s ease,transform .1s ease;cursor:pointer;text-align:center;margin:0 auto}
    /* Live Product Options outranks the class above for exactly these three
       properties - measured, not guessed. It scopes its swatch styling under a
       generated id (#cliqivqpfzsa .cl-po--swatch), so specificity alone cannot
       win and !important is required. Deliberately scoped to these three:
       background-color and box-shadow are left clean so the :hover and :active
       rules below can still take effect.

       This replaces a block of 20 inline !important declarations that also
       outranked those hover rules, leaving the button with no interaction
       feedback at all. - TK 16/08/2026 */
    .cl-po--swatch.cl-po--swatch-button.tdog-personalise{border-radius:9999px!important;border:none!important;padding:16px 28px!important}
    /* Hover and press are expressed through filter and transform, NOT
       background-color. - TK 16/08/2026

       Live Product Options writes its own swatch colour inline on this element
       ("color: rgb(255,255,255); background-color: rgb(222,106,193);"), and
       measurement on the preview theme showed that even an !important
       stylesheet rule failed to override it. box-shadow is contested too.
       filter and transform are untouched by the app, so they win cleanly and
       give the same visual affordance - the button darkens on hover and dips
       on press - without fighting a cascade we cannot win. */
    .tdog-personalise:hover{filter:brightness(0.93)}
    .tdog-personalise:active{filter:brightness(0.86);transform:translateY(1px)}
    .badge--new{background:#16a34a;color:#fff;font-weight:600;padding:.375rem .625rem;border-radius:9999px;line-height:1;display:inline-flex;align-items:center;white-space:nowrap;pointer-events:none;font-size:.875rem}
    .product-card__media .badge--new{position:absolute;top:.5rem;left:50%;transform:translateX(-50%);z-index:3}
    @media (max-width:768px){.product-card__media .badge--new{top:.625rem}}

    /* ===== Engraving UX v3: minimal + Concept-friendly ===== */
    :root{
      --tdog-neutral: rgba(0,0,0,.55);
      --tdog-muted: rgba(0,0,0,.12);
      --tdog-bg: rgba(0,0,0,.03);

      --tdog-warn: #F98027;
      --tdog-good: #05ADAE;
      --tdog-info: #F98027;
    }

    .tdog-engrave-progress{
      margin: 14px 0 12px;
      padding: 12px 14px;
      border-radius: 12px;
      border: 1px solid var(--tdog-muted);
      background: var(--tdog-bg);
    }
    .tdog-engrave-progress__top{
      display:flex;
      justify-content:space-between;
      gap:10px;
      align-items:baseline;
      margin-bottom:8px;
    }
    .tdog-engrave-progress__title{ font-weight: 800; font-size: 14px; }
    .tdog-engrave-progress__count{ font-weight: 700; font-size: 13px; color: var(--tdog-neutral); white-space:nowrap; }
    .tdog-engrave-progress__bar{
      height: 10px;
      border-radius: 9999px;
      overflow:hidden;
      background: rgba(0,0,0,.10);
      position: relative !important;
    }
    .tdog-engrave-progress__fill{
      height:100%;
      width:0%;
      border-radius:9999px;
      background: var(--tdog-info);
      display: block !important;
      flex: none !important;
      max-width: none !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
      transition: width .35s ease, background-color .25s ease;
    }
    .tdog-engrave-progress__hint{
      margin-top: 8px;
      font-size: 12.5px;
      color: rgba(0,0,0,.62);
      line-height: 1.35;
    }

    .tdog-engrave-progress.is-not-started .tdog-engrave-progress__fill{
      background:#d1d5db;
    }

    .tdog-engrave-progress.is-in-progress .tdog-engrave-progress__fill{
      background:var(--tdog-warn);
    }

    .tdog-engrave-progress.is-completed .tdog-engrave-progress__fill{
      background:var(--tdog-good);
    }
    .tdog-engrave-progress.is-not-started .tdog-engrave-progress__hint strong { color: rgba(0,0,0,.55); }
    .tdog-engrave-progress.is-in-progress .tdog-engrave-progress__hint strong { color: var(--tdog-info); }
    .tdog-engrave-progress.is-completed .tdog-engrave-progress__hint strong { color: var(--tdog-good); }

    .tdog-engrave-progress__text{
      font-weight:500;
      color:#4b5563;
    }
    .tdog-engrave-progress.is-completed .tdog-engrave-progress__text{
      color:var(--tdog-good);
    }

    .tdog-engrave-card{
      position: relative;
      margin: 12px 0;
      padding: 12px 14px;
      border-radius: 12px;
      border: 1px solid var(--tdog-muted);
      background: #F7F7F8;
      box-shadow: 0 1px 2px rgba(0,0,0,.04);
      overflow: hidden;
      transition: background-color .2s ease, border-color .2s ease, box-shadow .2s ease;
    }
    .tdog-engrave-card::before{
      content: "";
      position: absolute;
      top: 12px;
      bottom: 12px;
      left: 0;
      width: 4px;
      border-radius: 0 9999px 9999px 0;
      background: rgba(0,0,0,.18);
      pointer-events: none;
    }
    .tdog-engrave-card[data-state="not-started"]{
      background: #F7F7F8;
      border-color: rgba(0,0,0,.10);
    }
    .tdog-engrave-card[data-state="not-started"]::before{
      background: rgba(255, 238, 0, 1);
    }
    .tdog-engrave-card[data-state="in-progress"]{
      background: #FFF4EC;
      border-color: rgba(249,128,39,.20);
    }
    .tdog-engrave-card[data-state="in-progress"]::before{
      background: var(--tdog-warn);
    }
    .tdog-engrave-card[data-state="complete"],
    .tdog-engrave-card[data-state="skipped"]{
      background: #EDF9F9;
      border-color: rgba(5,173,174,.20);
    }
    .tdog-engrave-card[data-state="complete"]::before,
    .tdog-engrave-card[data-state="skipped"]::before{
      background: var(--tdog-good);
    }
    .tdog-engrave-card .cl-po--panel{
      padding-top: 8px;
    }
    .cl-po--collapse.tdog-engrave-collapse{
      display:flex;
      align-items:center;
      gap:12px;
      width:100%;
      flex-wrap:nowrap;
    }
    .cl-po--collapse.tdog-engrave-collapse::before{
      content:"";
      display:block;
      width:16px;
      min-width:16px;
      max-width:16px;
      height:16px;
      min-height:16px;
      max-height:16px;
      aspect-ratio:1 / 1;
      border-radius:50%;
      flex:0 0 16px;
      align-self:center;
      background: rgba(0,0,0,.18);
    }

    .tdog-engrave-badge{
      margin-left:auto;
      font-size:12px;
      font-weight:800;
      padding:4px 10px;
      border-radius:9999px;
      border:1px solid var(--tdog-muted);
      background: rgba(255,255,255,.7);
      color: rgba(0,0,0,.70);
      white-space:nowrap;
    }

    .cl-po--collapse.tdog-engrave-collapse[data-state="not-started"]::before{ background: rgba(255, 238, 0, 1); }
    .cl-po--collapse.tdog-engrave-collapse[data-state="in-progress"]::before{ background: var(--tdog-warn); }
    .cl-po--collapse.tdog-engrave-collapse[data-state="complete"]::before{ background: var(--tdog-good); }
    .cl-po--collapse.tdog-engrave-collapse[data-state="skipped"]::before{ background: #64748b; }

    .tdog-engrave-badge[data-state="not-started"]{ color: rgba(0,0,0,.65); }
    .tdog-engrave-badge[data-state="in-progress"]{ border-color: rgba(180,83,9,.35); color: var(--tdog-warn); }
    .tdog-engrave-badge[data-state="complete"]{ border-color: rgba(22,101,52,.35); color: var(--tdog-good); }
    .tdog-engrave-badge[data-state="skipped"]{
      border-color: rgba(100,116,139,.35);
      color: #64748b;
    }

    @media (max-width: 767px){
      .tdog-engrave-card{
        padding: 10px 12px;
        margin: 10px 0;
      }
      .cl-po--collapse.tdog-engrave-collapse{
        gap:10px;
      }
      .tdog-engrave-badge{
        flex-shrink:0;
      }
    }


    /* NOTE: an earlier version hid the text input, Font and Approve controls for
       declined items, because writing text into LPO's field made the app reveal
       them. That whole approach is gone - the field is left empty now, so LPO
       never discloses those controls and there is nothing to hide. Do not
       reintroduce the hiding: a hidden control that LPO still requires is what
       silently blocked checkout. - TK 16/08/2026 */

    /* Checkout gate: unresolved items after a blocked add-to-cart - TK 16/08/2026 */
    .tdog-engrave-card--error{
      background:#FFF1F2;
      border-color:rgba(225,29,72,.45);
    }
    .tdog-engrave-card--error::before{ background:#e11d48; }
    .tdog-engrave-alert{
      margin-top:10px;
      padding:10px 12px;
      border-radius:10px;
      border:1px solid rgba(225,29,72,.35);
      background:#FFF1F2;
      color:#9f1239;
      font-size:13px;
      font-weight:600;
      line-height:1.4;
    }
    .tdog-engrave-alert[hidden]{ display:none; }


    .tdog-engrave-row{
      display:flex;
      gap:10px;
      align-items:flex-start;
      margin: 10px 0 14px;
    }
    .tdog-engrave-row input[type="checkbox"]{ margin-top:2px; }

    .cl-po--font-selector .cl-po--swatch-container{
      display:grid!important;
      grid-template-columns:repeat(2,minmax(0,1fr))!important;
      gap:10px!important;
    }
    @media (min-width:720px){
      .cl-po--font-selector .cl-po--swatch-container{
        grid-template-columns:repeat(3,minmax(0,1fr))!important;
      }
    }
    .cl-po--font-selector .cl-po--swatch-item.grow{margin:0!important}
    .cl-po--font-selector .cl-po--swatch.cl-po--swatch-img.cl-po--large{width:100%!important;border-radius:12px!important;overflow:hidden!important}

    .cl-po--font-selector img{width:100%!important;height:auto!important;display:block!important}
    .cl-po--font-selector .cl-po--swatch-container .cl-po--break{
      display:none !important;
    }

    .cl-po--font-selector .cl-po--swatch-container{
      grid-auto-flow: row dense !important;
    }
    .cl-po--font-selector .cl-po--swatch-item{
      min-width: 0 !important;
    }
  `;

  function ensureStyle() {
    if (document.getElementById(CONFIG.styleId)) return;

    const tag = document.createElement("style");
    tag.id = CONFIG.styleId;
    tag.textContent = CSS;
    document.head.appendChild(tag);
    log("CSS injected");
  }

  /************************************************************
   * New badge configuration
   ************************************************************/
  const DEFAULTS = Object.freeze({
    enabled: true,
    days: 30,
    tags: ["new", "new-arrival", "new_in", "just-in", "fresh"]
  });

  function readNewBadgeConfig() {
    const config = {
      enabled: DEFAULTS.enabled,
      days: DEFAULTS.days,
      tags: [...DEFAULTS.tags]
    };

    const nodes = safeRun(
      "readNewBadgeConfig.query",
      () => Array.from(document.querySelectorAll(SELECTORS.newBadgeConfig)),
      []
    );

    for (const node of nodes) {
      try {
        const data = JSON.parse(node.textContent || "{}");

        if (typeof data.enabled === "boolean") {
          config.enabled = data.enabled;
        }

        if (Number.isFinite(data.days)) {
          config.days = data.days;
        }

        // Replace rather than merge. Merging meant the built-in keywords could
        // never be removed, only added to, so the theme setting could not turn
        // one off - which makes the setting a lie. - TK 16/08/2026
        if (Array.isArray(data.tags)) {
          const provided = data.tags
            .map((tag) => String(tag).trim().toLowerCase())
            .filter(Boolean);

          if (provided.length > 0) {
            config.tags = Array.from(new Set(provided));
          }
        }
      } catch (err) {
        warn("Invalid new badge config block ignored", err);
      }
    }

    return config;
  }

  const NEW_BADGE_CONFIG = readNewBadgeConfig();

  /************************************************************
   * Utilities
   ************************************************************/
  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  const qsa = (root, selector) => {
    const scope = root || document;
    const results = Array.from(scope.querySelectorAll(selector));

    if (scope instanceof Element && scope.matches(selector)) {
      results.unshift(scope);
    }

    return results;
  };

  function debounce(fn, delay = 80) {
    let timerId = 0;
    return (...args) => {
      window.clearTimeout(timerId);
      timerId = window.setTimeout(() => fn(...args), delay);
    };
  }

  function cssEscape(str) {
    return (window.CSS && typeof window.CSS.escape === "function")
      ? window.CSS.escape(str)
      : String(str).replace(/[^a-zA-Z0-9_\-]/g, (char) => `\\${char}`);
  }

  function parseUnixSeconds(value) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
  }

  function moneyToCents(moneyStr) {
    if (!moneyStr) return null;

    const cleaned = String(moneyStr).replace(/[^\d.,-]/g, "").trim();
    if (!cleaned) return null;

    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    const normalized = lastComma > lastDot
      ? cleaned.replace(/\./g, "").replace(",", ".")
      : cleaned.replace(/,/g, "");

    const value = Number(normalized);
    return Number.isNaN(value) ? null : Math.round(value * 100);
  }

  /*
   * Returns "" when the price cannot be formatted with confidence, and callers
   * must treat that as "leave the price alone". - TK 16/08/2026
   *
   * This used to fall back to "USD" on an AUD store, and to a bare unformatted
   * number when Intl threw. Both put a wrong-looking price on the buy button,
   * which is a worse outcome than showing the theme's own correct one.
   */
  function centsToMoney(cents) {
    if (typeof cents !== "number") return "";

    const currency = window.Shopify?.currency?.active;
    if (typeof currency !== "string" || currency.length !== 3) {
      warn("centsToMoney: no active currency code; leaving price untouched");
      return "";
    }

    try {
      const locale = window.Shopify?.locale || navigator.language || "en-AU";

      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (err) {
      warn("centsToMoney formatting failed; leaving price untouched", err);
      return "";
    }
  }

  function isElement(value) {
    return value instanceof Element;
  }

  function createElement(tagName, options = {}) {
    const element = document.createElement(tagName);

    if (options.className) {
      element.className = options.className;
    }

    if (options.text != null) {
      element.textContent = options.text;
    }

    if (options.attributes) {
      for (const [name, value] of Object.entries(options.attributes)) {
        if (value != null) {
          element.setAttribute(name, String(value));
        }
      }
    }

    return element;
  }

  /*
   * Write guards - TK 16/08/2026
   *
   * Every one of these returns early when the value is already what we want.
   * That matters more than it looks: this script runs inside MutationObservers
   * that watch the same nodes it writes to, so an unconditional write re-triggers
   * the observer that caused it and the pair spin forever. Measured at ~430
   * mutations/sec on a 3-item bundle before these were added. Never write to the
   * DOM in this file without going through one of these.
   *
   * In particular, classList.add() is NOT a no-op when the class is already
   * present: per spec it re-serialises the token set and sets the attribute
   * regardless, and setAttribute queues a mutation record even when the value is
   * byte-identical. Three such calls kept this loop alive at 432 mutations/3s
   * after the first round of guards looked like it had fixed it. Use
   * setClassIfChanged, never classList.add. - TK 16/08/2026
   */
  function setTextIfChanged(element, value) {
    if (!element || element.textContent === value) return false;
    element.textContent = value;
    return true;
  }

  function setDataIfChanged(element, key, value) {
    if (!element || element.dataset[key] === value) return false;
    element.dataset[key] = value;
    return true;
  }

  function setAttrIfChanged(element, name, value) {
    if (!element || element.getAttribute(name) === value) return false;
    element.setAttribute(name, value);
    return true;
  }

  function setStyleIfChanged(element, property, value) {
    if (!element || element.style.getPropertyValue(property) === value) return false;
    element.style.setProperty(property, value);
    return true;
  }

  function setClassIfChanged(element, className, shouldHave) {
    if (!element || element.classList.contains(className) === shouldHave) return false;
    element.classList.toggle(className, shouldHave);
    return true;
  }

  function appendTextWithStrong(container, strongText, trailingText) {
    if (!container) return false;

    const signature = `${strongText}|${trailingText}`;
    if (container.dataset.tdogContent === signature) return false;
    container.dataset.tdogContent = signature;

    const strong = document.createElement("strong");
    strong.textContent = strongText;

    container.replaceChildren(strong, document.createTextNode(` ${trailingText}`));
    return true;
  }

  function getRelevantRootsFromMutations(mutations) {
    const roots = new Set();

    for (const mutation of mutations) {
      const target = mutation.target;
      if (isElement(target)) {
        roots.add(target);
      }

      for (const node of mutation.addedNodes) {
        if (isElement(node)) {
          roots.add(node);
        }
      }
    }

    return roots.size > 0 ? Array.from(roots) : [document];
  }

  /*
   * Observer registry - TK 16/08/2026
   *
   * A MutationObserver keeps the node it observes alive, so an observer left
   * running against a section Shopify has since replaced leaks both the
   * observer and the whole detached subtree it watches. That happens on every
   * shopify:section:load in the theme editor, and on any app-driven section
   * re-render on the storefront. Nothing here ever disconnected.
   */
  const liveObservers = new Set();

  function registerObserver(observer, target) {
    liveObservers.add({ observer, target });
    return observer;
  }

  function pruneDetachedObservers() {
    let disconnected = 0;

    for (const record of liveObservers) {
      if (document.contains(record.target)) continue;

      record.observer.disconnect();
      liveObservers.delete(record);
      disconnected += 1;
    }

    if (disconnected > 0) {
      log(`Disconnected ${disconnected} observer(s) watching detached nodes`);
    }
  }

  function elementMatchesOrContains(root, selector) {
    if (!isElement(root)) return false;
    return root.matches(selector) || !!root.querySelector(selector);
  }

  /************************************************************
   * Feature A: Personalise button styling
   ************************************************************/
  const processedButtons = new WeakSet();

  function stylePersonaliseButtons(root = document) {
    safeRun("stylePersonaliseButtons", () => {
      const buttons = qsa(root, SELECTORS.personaliseButton);
      let styledCount = 0;

      for (const button of buttons) {
        if (processedButtons.has(button)) {
          continue;
        }

        button.classList.add("tdog-personalise");

        processedButtons.add(button);
        styledCount += 1;
      }

      if (styledCount > 0) {
        log(`Styled ${styledCount} personalise button(s)`);
      }
    });
  }

  /************************************************************
   * Feature B: NEW badge logic
   ************************************************************/
  const processedCards = new WeakSet();

  /*
   * Match whole tags and whole words within them, never bare substrings.
   * - TK 16/08/2026
   *
   * This used to lowercase the entire comma-separated string and run
   * includes() over it, so any tag merely CONTAINING a keyword matched.
   * "freshwater" contains "fresh"; on a fishing store that would have put a
   * New badge on half the catalogue the day someone added that tag. It has
   * not bitten yet only because no such tag exists today.
   *
   * Word-level matching is deliberate rather than strict equality: it keeps
   * the live "New Offers" tag badged, which equality would have silently
   * switched off.
   */
  function hasNewTag(csv, tags) {
    if (!csv) return false;

    const productTags = String(csv)
      .toLowerCase()
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);

    return productTags.some((productTag) => {
      const words = productTag.split(/[^a-z0-9]+/).filter(Boolean);
      return tags.some((tag) => productTag === tag || words.includes(tag));
    });
  }

  function isNew(card) {
    return safeRun("isNew", () => {
      if (!NEW_BADGE_CONFIG.enabled) return false;

      const tagsCsv = card?.dataset?.productTags ?? "";
      if (hasNewTag(tagsCsv, NEW_BADGE_CONFIG.tags)) {
        return true;
      }

      const created = parseUnixSeconds(card?.dataset?.productCreated);
      const published = parseUnixSeconds(card?.dataset?.productPublished);
      const timestamp = Math.max(created, published);

      if (!timestamp) return false;

      const ageDays = (Date.now() - (timestamp * 1000)) / MS_PER_DAY;
      return ageDays >= 0 && ageDays <= NEW_BADGE_CONFIG.days;
    }, false);
  }

  function createNewBadge() {
    return createElement("span", {
      className: "badge badge--new flex items-center font-medium leading-none rounded-full",
      text: "New",
      attributes: { "aria-label": "New product" }
    });
  }

  function placeNewBadges(root = document) {
    if (!NEW_BADGE_CONFIG.enabled) return;

    safeRun("placeNewBadges", () => {
      const cards = qsa(root, SELECTORS.productCard);
      let placedCount = 0;

      for (const card of cards) {
        if (processedCards.has(card)) {
          continue;
        }

        if (!isNew(card)) {
          processedCards.add(card);
          continue;
        }

        const media = card.querySelector(SELECTORS.productCardMedia);
        if (!media) {
          processedCards.add(card);
          continue;
        }

        if (!media.querySelector(".badge--new")) {
          media.appendChild(createNewBadge());
          placedCount += 1;
        }

        processedCards.add(card);
      }

      if (placedCount > 0) {
        log(`Placed ${placedCount} NEW badge(s)`);
      }
    });
  }

  /************************************************************
   * Feature C: Rapi Bundles
   ************************************************************/
  const boundRapiSections = new WeakSet();
  const rapiObserverByRoot = new WeakMap();
  const defaultButtonLabelBySection = new WeakMap();

  function hasRapiBundle(root = document) {
    const found = !!root.querySelector(SELECTORS.rapiRoot);
    log("hasRapiBundle?", found);
    return found;
  }

  /*
   * Only pay for the Rapi watch where a bundle bar can actually appear.
   * - TK 16/08/2026
   *
   * waitForRapi opens a MutationObserver over the entire document plus a 250ms
   * polling interval, for up to 10 seconds. That was running on every page of
   * the site - collections, blog, cart, account, search - when Rapi renders its
   * bar on a handful of products only. Each poll runs a
   * [class*='rapi-bundle-content'] attribute-substring selector over the whole
   * document, which is the expensive kind.
   *
   * Skipping it is safe rather than merely cheap: the global observer in boot()
   * already calls bindAllRapiSections whenever a Rapi root or product form
   * turns up, so a late-rendering or dynamically-injected bundle is still
   * bound. This wait was only ever a head start on the same work.
   */
  function shouldWatchForRapi() {
    if (hasRapiBundle()) return true;

    const isProductPage = document.body?.classList?.contains("template-product") === true;
    const hasProductSection = !!document.querySelector(SELECTORS.productFormWrapper);

    return isProductPage || hasProductSection;
  }

  function waitForRapi({ timeoutMs = CONFIG.waitForRapiTimeoutMs, pollMs = CONFIG.waitForRapiPollMs } = {}) {
    return new Promise((resolve) => {
      if (hasRapiBundle()) {
        resolve(true);
        return;
      }

      let resolved = false;
      const startTime = Date.now();

      const finish = (value) => {
        if (resolved) return;
        resolved = true;
        observer.disconnect();
        window.clearInterval(intervalId);
        resolve(value);
      };

      const observer = new MutationObserver(() => {
        if (hasRapiBundle()) {
          log("Rapi detected via MutationObserver");
          finish(true);
        }
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });

      const intervalId = window.setInterval(() => {
        if (hasRapiBundle()) {
          log("Rapi detected via polling");
          finish(true);
          return;
        }

        if ((Date.now() - startTime) > timeoutMs) {
          warn("Timed out waiting for Rapi");
          finish(false);
        }
      }, pollMs);
    });
  }

  function getFormAndId(sectionEl) {
    const form = sectionEl.querySelector(SELECTORS.productForm);
    const formId = form?.getAttribute("id") || null;
    return { form, formId };
  }

  function getBuyButtonByFormId(formId) {
    if (!formId) return null;
    return document.querySelector(SELECTORS.buyButtonByFormId(formId));
  }

  function getBuyPriceElByFormId(formId) {
    if (!formId) return null;

    const container = document.querySelector(SELECTORS.buyPriceContainerByFormId(formId));
    return container?.querySelector("product-buy-price.price")
      || getBuyButtonByFormId(formId)?.querySelector("product-buy-price.price")
      || null;
  }

  function getBuyLabelElByFormId(formId) {
    return getBuyButtonByFormId(formId)?.querySelector(SELECTORS.buyButtonLabel) || null;
  }

  // The theme disables the buy button for sold-out / unavailable variants.
  // Treat "can't find the button" as not purchasable so we stay quiet rather
  // than guess. - TK 16/08/2026
  function isPurchasable(formId) {
    const button = getBuyButtonByFormId(formId);
    if (!button) return false;
    return !button.disabled && button.getAttribute("aria-disabled") !== "true";
  }

  function getRapiScope(sectionEl) {
    if (sectionEl?.querySelector(".rapi-bundles, [class*='rapi-bundle-content']")) {
      return sectionEl;
    }

    const nearestProduct = sectionEl?.closest?.("product-info, .product, .product-main, main") || null;
    if (nearestProduct?.querySelector(".rapi-bundles, [class*='rapi-bundle-content']")) {
      return nearestProduct;
    }

    return document;
  }

  function getCheckedBundleRadio(sectionEl) {
    const scope = getRapiScope(sectionEl);
    const radios = Array.from(scope.querySelectorAll(".rapi-bundles__bar input[type='radio']"));
    return radios.find((radio) => radio.checked) || null;
  }

  function isVisible(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
  }

  function getSelectedBundleContainer(sectionEl) {
    const scope = getRapiScope(sectionEl);
    const containers = Array.from(scope.querySelectorAll(".rapi-bundles__bar-container"));
    if (containers.length === 0) {
      return null;
    }

    const checkedRadio = getCheckedBundleRadio(sectionEl);
    if (checkedRadio?.id) {
      const checkedLabel = scope.querySelector(`label[for="${cssEscape(checkedRadio.id)}"]`);
      if (checkedLabel) {
        log("Resolved selected Rapi container via checked radio ->", checkedRadio.id);
        return checkedLabel;
      }
    }

    let bestContainer = null;
    let bestScore = -1;

    for (const container of containers) {
      let score = 0;
      const forId = container.getAttribute("for");
      const linkedRadio = forId ? scope.querySelector(`#${cssEscape(forId)}`) : null;
      const parentBar = container.closest(".rapi-bundles__bar");

      if (linkedRadio?.checked) {
        score += 1000;
      }

      const visibleExpandedBlocks = Array.from(container.querySelectorAll(".rapi--bb-royal-container, .rapi-content-inner-wrapper, .rapi-content-outer-wrapper"))
        .filter((el) => isVisible(el) && /add another|offer-image-preview|rapi--bb-royal/i.test(`${el.className} ${el.textContent || ""}`));
      if (visibleExpandedBlocks.length > 0) {
        score += 300;
      }

      if (container.querySelector(".rapi-pressable-button")) {
        score += 100;
      }

      const style = `${container.getAttribute("style") || ""} ${parentBar?.getAttribute("style") || ""}`.toLowerCase();
      if (style.includes("--bar-highlight-size")) {
        score += 50;
      }
      if (style.includes("box-shadow") && style.includes("inset")) {
        score += 40;
      }
      if (style.includes("--bar-selected-border-color") || style.includes("--bar-border-color: #05adaeff;")) {
        score += 20;
      }

      const radioVisual = container.querySelector(".rapi-bundles__bar-radio");
      if (radioVisual) {
        const rvStyle = `${radioVisual.getAttribute("style") || ""} ${window.getComputedStyle(radioVisual).cssText || ""}`.toLowerCase();
        if (rvStyle.includes("background") || rvStyle.includes("border")) {
          score += 5;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestContainer = container;
      }
    }

    /*
     * Require a strong signal, not merely the best of a weak field.
     * - TK 16/08/2026
     *
     * The threshold used to be > 0, which any container could clear on the +5
     * "its radio visual has a background or border" rule - true of every
     * container, selected or not. That meant an essentially arbitrary pick,
     * which then drove the buy button's price and label.
     *
     * MIN_SCORE sits above the +100/+50/+40/+20/+5 cosmetic signals, so only a
     * checked linked radio (+1000) or a genuinely expanded content block (+300)
     * qualifies. Everything here reads Rapi's private CSS internals - inline
     * custom properties and a hardcoded hex - so it will break silently on any
     * app update. Failing closed means we leave the theme's own correct price
     * and label alone, which is the safe direction.
     */
    const MIN_SCORE = 300;

    if (bestContainer && bestScore >= MIN_SCORE) {
      log("Resolved selected Rapi container via heuristic fallback ->", {
        forId: bestContainer.getAttribute("for"),
        score: bestScore
      });
      return bestContainer;
    }

    if (bestContainer) {
      log(`Declined weak Rapi container match (score ${bestScore} < ${MIN_SCORE}); leaving theme price and label untouched`);
    }

    return null;
  }

  function getBundleLabelForRadio(sectionEl, radio) {
    if (!radio?.id) return null;
    return getRapiScope(sectionEl).querySelector(`label[for="${cssEscape(radio.id)}"]`);
  }

  function getSelectedBundleTitle(sectionEl) {
    const label = getSelectedBundleContainer(sectionEl);
    return label?.querySelector(SELECTORS.bundleTitle)?.textContent?.trim() || null;
  }

  // Returns { cents, formatted } or null. `formatted` is the app's own money
  // string when we have one, and null when we only recovered raw cents.
  function getSelectedBundlePrice(sectionEl) {
    const label = getSelectedBundleContainer(sectionEl);
    if (!label) {
      log("Skipped bundle price lookup: selected bundle container not found");
      return null;
    }

    /*
     * Only two sources are trusted, both of which are unambiguously "the price
     * of this bundle": the app's own price element, and an explicit integer
     * data-price. - TK 16/08/2026
     *
     * This previously swept .price, .money, [class*='price'] and finally ran a
     * currency regex over the label's entire text content, taking the first
     * match. On a label containing a was/now pair, a per-unit price or a saving,
     * that could latch onto the wrong number and print it on the buy button -
     * a price the cart would then not honour. Showing no override at all leaves
     * the theme's own correct price in place, which is strictly better.
     */
    const directText = label.querySelector(SELECTORS.bundlePrice)?.textContent?.trim() || "";
    const directCents = moneyToCents(directText);
    if (directCents != null) {
      // Carry the app's own already-formatted string through rather than
      // rebuilding it. See updateBuyButtonPrice. - TK 16/08/2026
      return { cents: directCents, formatted: directText };
    }

    for (const candidate of label.querySelectorAll("[data-price]")) {
      if (candidate.closest('s, del, .line-through, [style*="line-through"]')) {
        continue;
      }

      const dataPrice = candidate.getAttribute("data-price");
      if (dataPrice && /^\d+$/.test(dataPrice)) {
        return { cents: Number(dataPrice), formatted: null };
      }
    }

    log("Skipped bundle price lookup: no trustworthy bundle price found");
    return null;
  }

  function ensureDefaultLabelCached(sectionEl) {
    if (defaultButtonLabelBySection.has(sectionEl)) {
      return;
    }

    const { formId } = getFormAndId(sectionEl);
    const labelEl = getBuyLabelElByFormId(formId);

    if (labelEl) {
      defaultButtonLabelBySection.set(sectionEl, labelEl.textContent.trim());
    }
  }

  function updateBuyButtonPrice(sectionEl) {
    const { formId } = getFormAndId(sectionEl);
    if (!formId) {
      log("Skipped Buy Button Price update: formId not found");
      return false;
    }

    const price = getSelectedBundlePrice(sectionEl);
    if (!price) {
      log("Skipped Buy Button Price update: bundle price not available yet");
      return false;
    }

    const priceEl = getBuyPriceElByFormId(formId);
    if (!priceEl) {
      log("Skipped Buy Button Price update: buy price element not available yet for", formId);
      return false;
    }

    /*
     * Prefer the app's own money string over reformatting the cents.
     * - TK 16/08/2026
     *
     * Intl.NumberFormat renders AUD as "A$17.96" under most locales, so the
     * buy button read "A$17.96" while the Rapi bar directly above it and the
     * theme's own price both read "$17.96". Reusing the source string keeps
     * the two consistent and removes the currency and locale guesswork
     * entirely. centsToMoney survives only for the data-price path, where
     * there is no string to reuse.
     */
    const formattedPrice = price.formatted || centsToMoney(price.cents);
    if (!formattedPrice) {
      log("Skipped Buy Button Price update: price could not be formatted with confidence");
      return false;
    }

    setAttrIfChanged(priceEl, "data-price", String(price.cents));
    setTextIfChanged(priceEl, formattedPrice);
    log("Updated Buy Button Price ->", formattedPrice);
    return true;
  }

  function updateBuyButtonLabel(sectionEl) {
    ensureDefaultLabelCached(sectionEl);

    const { formId } = getFormAndId(sectionEl);
    const labelEl = getBuyLabelElByFormId(formId);
    if (!labelEl) {
      log("Skipped Buy Button Label update: label element not available yet for", formId);
      return false;
    }

    /*
     * Never fight the theme over an unavailable variant. - TK 16/08/2026
     *
     * The theme's variant-picker writes "Sold out" / "Unavailable" /
     * "Pre-order" into this same element from theme.variantStrings on every
     * variant change. Whoever wrote last used to win, so a sold-out variant
     * could end up reading "Add X bundle to cart", inviting a click that cannot
     * succeed. The retry ladder in scheduleRapiSyncRetries suggests this race
     * was already being fought.
     */
    if (!isPurchasable(formId)) {
      log("Skipped Buy Button Label update: variant is not purchasable");
      return false;
    }

    const bundleTitle = getSelectedBundleTitle(sectionEl);
    const baseLabel = defaultButtonLabelBySection.get(sectionEl) || "Add to cart";

    if (bundleTitle) {
      const hasBundleWord = /bundle/i.test(bundleTitle);
      setTextIfChanged(labelEl, `Add ${bundleTitle}${hasBundleWord ? "" : " bundle"} to cart`);
    } else {
      setTextIfChanged(labelEl, baseLabel);
    }

    log("Updated Buy Button Label ->", labelEl.textContent);
    return true;
  }

  function syncRapiUI(sectionEl) {
    return safeRun("syncRapiUI", () => {
      const priceUpdated = updateBuyButtonPrice(sectionEl);
      const labelUpdated = updateBuyButtonLabel(sectionEl);
      return { priceUpdated: !!priceUpdated, labelUpdated: !!labelUpdated };
    }, { priceUpdated: false, labelUpdated: false });
  }

  function scheduleRapiSyncRetries(sectionEl, delays = [150, 400, 900, 1600]) {
    for (const delayMs of delays) {
      window.setTimeout(() => {
        const result = syncRapiUI(sectionEl);
        if (!result?.priceUpdated || !result?.labelUpdated) {
          log(`Rapi retry sync @ ${delayMs}ms`, result);
        }
      }, delayMs);
    }
  }

  function bindRapiSection(sectionEl) {
    if (!sectionEl || boundRapiSections.has(sectionEl)) {
      return;
    }

    boundRapiSections.add(sectionEl);

    const triggerUpdate = debounce(
      () => syncRapiUI(sectionEl),
      CONFIG.rapiSyncDebounceMs
    );

    sectionEl.addEventListener("change", (event) => {
      const target = event.target;
      if (!isElement(target)) return;

      if (
        target.matches(SELECTORS.checkedBundleRadio.replace(":checked", "")) ||
        target.matches(SELECTORS.bundleVariantSelect) ||
        !!target.closest(SELECTORS.bundleVariantSelect)
      ) {
        triggerUpdate();
      }
    });

    sectionEl.addEventListener("click", (event) => {
      const target = event.target;
      if (!isElement(target)) return;

      if (
        target.closest(".rapi-bundles__bar-container") ||
        target.closest(".rapi-bundles__bar") ||
        target.closest(".rapi-pressable-button")
      ) {
        triggerUpdate();
        window.setTimeout(() => syncRapiUI(sectionEl), 0);
        window.setTimeout(() => syncRapiUI(sectionEl), 120);
        window.setTimeout(() => syncRapiUI(sectionEl), 300);
      }
    }, true);

    const rapiRoot = sectionEl.querySelector(SELECTORS.rapiRoot);
    if (rapiRoot && !rapiObserverByRoot.has(rapiRoot)) {
      const observer = new MutationObserver(triggerUpdate);
      observer.observe(rapiRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      });
      rapiObserverByRoot.set(rapiRoot, registerObserver(observer, rapiRoot));
    }

    triggerUpdate();
    scheduleRapiSyncRetries(sectionEl);
  }

  function bindAllRapiSections(root = document) {
    const sections = qsa(root, SELECTORS.productFormWrapper);
    if (sections.length > 0) {
      log(`Found ${sections.length} product-form-wrapper section(s) to bind`);
    }

    for (const section of sections) {
      bindRapiSection(section);
    }
  }

  /************************************************************
   * Feature D: Live Product Options (Engraving bundle UX)
   ************************************************************/
  const EngraveUX = (() => {
    const CLASSES = Object.freeze({
      noEngrave: "tdog-no-engrave",
      card: "tdog-engrave-card",
      badge: "tdog-engrave-badge",
      collapse: "tdog-engrave-collapse",
      declineField: "tdog-decline-field",
      progress: "tdog-engrave-progress",
      count: "tdog-engrave-progress__count",
      fill: "tdog-engrave-progress__fill",
      hint: "tdog-engrave-progress__hint"
    });

    /*
     * Written into the Live Product Options text field when a customer declines
     * engraving, so the choice reaches Shopify as a line item property. An empty
     * field is dropped by Shopify entirely, which makes "deliberately declined"
     * and "never noticed the field" identical on the order - the exact ambiguity
     * our laser T&Cs rely on being able to settle. - TK 16/08/2026
     *
     * MUST fit the LPO field's maxlength, which is 20. The original wording,
     * "No Engraving Selected" (21), was silently clipped to
     * "No Engraving Selecte" on a real cart line item. Keep any replacement at
     * 20 characters or fewer and re-check against a live product, because the
     * truncation is silent - nothing errors, the order just reads wrong.
     */
    const NO_ENGRAVING_VALUE = "No Engraving Chosen";

    const processedGroups = new WeakSet();
    const boundWrappers = new WeakSet();
    const engraveObserverByWrapper = new WeakMap();
    let generatedIdCounter = 0;

    function nextGeneratedId(prefix) {
      generatedIdCounter += 1;
      return `${prefix}-${generatedIdCounter}`;
    }

    function getMainProductForm(root = document) {
      return root.querySelector('form[action*="/cart/add"]')
        || root.querySelector("product-form form")
        || root.querySelector("form.product-form")
        || root.querySelector("form");
    }

    function getWrapperScoped(root = document) {
      const form = getMainProductForm(root);

      if (!form) {
        return root.querySelector(SELECTORS.engraveWrapper) || document.querySelector(SELECTORS.engraveWrapper);
      }

      return form.closest("section, .shopify-section, .product")?.querySelector(SELECTORS.engraveWrapper)
        || form.parentElement?.querySelector(SELECTORS.engraveWrapper)
        || root.querySelector(SELECTORS.engraveWrapper)
        || document.querySelector(SELECTORS.engraveWrapper);
    }

    function getGroups(wrapper) {
      if (!wrapper) return [];

      return qsa(wrapper, SELECTORS.engraveGroup).filter((group) =>
        !!group.querySelector(SELECTORS.engraveCollapse) && !!group.querySelector(SELECTORS.engravePanel)
      );
    }

    function getCollapse(groupEl) {
      return groupEl.querySelector(SELECTORS.engraveCollapse);
    }

    function getPanel(groupEl) {
      return groupEl.querySelector(SELECTORS.engravePanel);
    }

    function findTextInput(groupEl) {
      return getPanel(groupEl)?.querySelector(SELECTORS.engraveTextInput) || null;
    }

    /*
     * Every text field in the group, not just the first. - TK 23/08/2026
     *
     * findTextInput uses querySelector, so it only ever returns one field.
     * Some products configure two engraving lines, and clearing only the
     * first left the second populated - LPO then still saw engraving text,
     * demanded its approval tick, and refused the add to cart with no
     * message. Anything that clears or watches engraving input must cover
     * all of them.
     */
    function getAllTextInputs(groupEl) {
      const panel = getPanel(groupEl);
      return panel ? Array.from(panel.querySelectorAll(SELECTORS.engraveTextInput)) : [];
    }

    function clearGroupText(groupEl) {
      let cleared = 0;

      for (const input of getAllTextInputs(groupEl)) {
        if (!input.value) continue;

        input.value = "";
        dispatchInputAndChange(input);
        cleared += 1;
      }

      if (cleared > 0) log(`Cleared ${cleared} engraving field(s) after decline`);
      return cleared;
    }

    function getFontSelected(groupEl) {
      return !!groupEl.querySelector(SELECTORS.engraveFontSelected);
    }

    // On this store the font picker is progressively disclosed - it only exists
    // once the customer types engraving text. A group with no picker at all must
    // not be treated as incomplete, or the gate below could never open for it.
    // - TK 16/08/2026
    function hasFontPicker(groupEl) {
      return !!groupEl.querySelector(SELECTORS.engraveFontAny);
    }

    function getApproveCheckbox(groupEl) {
      return groupEl.querySelector(SELECTORS.engraveApproveCheckbox);
    }

    function getNoEngraveCheckbox(groupEl) {
      return groupEl.querySelector(`input.${CLASSES.noEngrave}`);
    }

    function deriveItemName(groupEl) {
      const raw = String(groupEl.getAttribute("data-option") || "").trim();
      return raw.replace(/\s+/g, " ").replace(/\s*Group\s*$/i, "").trim() || "Item";
    }

    function setCollapseTitle(collapseEl, groupEl) {
      if (!collapseEl || collapseEl.dataset.tdogTitle === "1") {
        return;
      }

      collapseEl.dataset.tdogTitle = "1";

      const newTitle = `${deriveItemName(groupEl)} engraving (optional)`;
      const childNodes = Array.from(collapseEl.childNodes);
      const hasElementChildren = childNodes.some((node) => node.nodeType === Node.ELEMENT_NODE);

      if (!hasElementChildren) {
        collapseEl.textContent = newTitle;
        return;
      }

      const firstTextNode = childNodes.find((node) => node.nodeType === Node.TEXT_NODE);
      if (firstTextNode) {
        firstTextNode.textContent = ` ${newTitle} `;
      } else {
        collapseEl.insertAdjacentText("afterbegin", newTitle);
      }
    }

    function ensureBadge(collapseEl) {
      if (!collapseEl) return null;

      setClassIfChanged(collapseEl, CLASSES.collapse, true);

      let badge = collapseEl.querySelector(`.${CLASSES.badge}`);
      if (badge) return badge;

      badge = createElement("span", {
        className: CLASSES.badge,
        text: "Not started"
      });
      badge.dataset.state = "not-started";
      collapseEl.appendChild(badge);

      return badge;
    }

    const BADGE_TEXT = Object.freeze({
      "complete": "Complete",
      "in-progress": "In progress",
      "skipped": "No engraving",
      "not-started": "Not started"
    });

    function setBadge(collapseEl, state) {
      const badge = ensureBadge(collapseEl);
      if (!badge) return;

      setDataIfChanged(collapseEl, "state", state);
      setDataIfChanged(badge, "state", state);
      setDataIfChanged(collapseEl.closest(`.${CLASSES.card}`), "state", state);
      setTextIfChanged(badge, BADGE_TEXT[state] || BADGE_TEXT["not-started"]);
    }

    function getGroupState(groupEl) {
      const noEngraveCheckbox = getNoEngraveCheckbox(groupEl);
      if (noEngraveCheckbox?.checked) {
        return "skipped";
      }

      const textInput = findTextInput(groupEl);
      const textValue = (textInput?.value || "").trim();
      const approveCheckbox = getApproveCheckbox(groupEl);

      // Only require a control that actually exists in this group. Requiring a
      // font when no picker is present would make the group permanently
      // incomplete and block checkout outright. - TK 16/08/2026
      const fontOk = hasFontPicker(groupEl) ? getFontSelected(groupEl) : true;
      const approveOk = approveCheckbox ? approveCheckbox.checked : true;

      const started = !!textValue || getFontSelected(groupEl) || !!approveCheckbox?.checked;
      if (!started) {
        return "not-started";
      }

      const complete = !!textValue && fontOk && approveOk;
      return complete ? "complete" : "in-progress";
    }

    function createProgressElement() {
      const root = createElement("div", { className: CLASSES.progress });

      const top = createElement("div", { className: "tdog-engrave-progress__top" });
      const title = createElement("div", { className: "tdog-engrave-progress__title", text: "Engraving" });
      const count = createElement("div", { className: CLASSES.count, text: "0 / 0 completed" });

      top.append(title, count);

      const bar = createElement("div", { className: "tdog-engrave-progress__bar" });
      const fill = createElement("div", { className: CLASSES.fill });
      bar.appendChild(fill);

      const hint = createElement("div", { className: CLASSES.hint });
      hint.textContent = "This bundle has engraving options for multiple items. Expand each section to personalise, or tick “No engraving for this item”.";

      root.append(top, bar, hint);
      return root;
    }

    function ensureProgress(wrapper) {
      let progress = wrapper.querySelector(`.${CLASSES.progress}`);
      if (progress) return progress;

      progress = createProgressElement();

      const firstGroup = wrapper.querySelector(SELECTORS.engraveGroup);
      (firstGroup || wrapper).insertAdjacentElement(firstGroup ? "beforebegin" : "afterbegin", progress);

      return progress;
    }

    function updateProgress(wrapper, groups) {
      const progress = ensureProgress(wrapper);
      const countEl = progress.querySelector(`.${CLASSES.count}`);
      const fillEl = progress.querySelector(`.${CLASSES.fill}`);
      const hintEl = progress.querySelector(`.${CLASSES.hint}`);
      if (!countEl || !fillEl || !hintEl) return;

      const total = groups.length;
      const completed = groups.reduce((count, group) => {
        const state = getGroupState(group);
        return count + ((state === "complete" || state === "skipped") ? 1 : 0);
      }, 0);

      const remaining = Math.max(total - completed, 0);
      const percentage = total > 0 ? (completed / total) * 100 : 0;

      let label = "Completed";
      let message = "All items confirmed. Your engraving selections are ready.";

      if (completed === 0) {
        label = "Not started";
        message = "Choose engraving or ‘No engraving’ for each item.";
      } else if (completed < total) {
        label = "In progress";
        message = `You still have ${remaining} item${remaining === 1 ? "" : "s"} to confirm.`;
      }

      setTextIfChanged(countEl, `${completed} / ${total} completed`);
      setStyleIfChanged(fillEl, "width", `${percentage}%`);
      appendTextWithStrong(hintEl, `${label}.`, message);

      // Fill colour comes from the .is-* classes in CSS. It used to also be set
      // inline, which silently overrode those rules and made them dead. - TK 16/08/2026
      const stateClass = completed === 0
        ? "is-not-started"
        : completed < total
          ? "is-in-progress"
          : "is-completed";

      setClassIfChanged(progress, "is-not-started", stateClass === "is-not-started");
      setClassIfChanged(progress, "is-in-progress", stateClass === "is-in-progress");
      setClassIfChanged(progress, "is-completed", stateClass === "is-completed");

      return { total, completed, remaining };
    }

    function dispatchInputAndChange(element) {
      element.dispatchEvent(new Event("input", { bubbles: true }));
      element.dispatchEvent(new Event("change", { bubbles: true }));
    }

    /*
     * Record the decline on our OWN hidden field, never in LPO's. - TK 16/08/2026
     *
     * The first version of this wrote NO_ENGRAVING_VALUE into the Live Product
     * Options text input. That looked tidy but broke checkout: LPO requires its
     * "I have checked my text and have read the engraving instructions" tick
     * whenever its text field is non-empty, and enforces it from a capture-phase
     * click listener on the .product container - so a declined item demanded an
     * approval the customer could not give, because we hid that control. The
     * click was swallowed with no message at all. Live bug, blocked every bundle
     * where a customer declined an item.
     *
     * It survived testing because form.requestSubmit() never runs LPO's click
     * handler, so every automated check sailed past the validation that real
     * customers hit.
     *
     * So: LPO's field goes back to being empty on decline, exactly as it was
     * before this feature existed, and the customer's choice is carried by a
     * hidden input of our own that LPO neither manages nor validates. It binds
     * to the product form the same way LPO's inputs do, via the form attribute,
     * and Shopify records it as an ordinary line item property.
     *
     * Note this field has no maxlength, so the wording is unconstrained here.
     */
    function setDeclineProperty(groupEl, textInput, declined) {
      let field = groupEl.querySelector(`input.${CLASSES.declineField}`);

      if (!declined) {
        field?.remove();
        return;
      }

      if (!field) {
        field = createElement("input", {
          className: CLASSES.declineField,
          attributes: { type: "hidden" }
        });

        const formId = textInput.getAttribute("form");
        if (formId) field.setAttribute("form", formId);

        groupEl.appendChild(field);
      }

      field.setAttribute("name", `properties[${deriveItemName(groupEl)} Engraving]`);
      field.value = NO_ENGRAVING_VALUE;
    }

    function ensureNoEngraveRow(groupEl, wrapper) {
      const panel = getPanel(groupEl);
      if (!panel) return;

      if (panel.querySelector(`input.${CLASSES.noEngrave}`)) {
        return;
      }

      const textInput = findTextInput(groupEl);
      if (!textInput) {
        return;
      }

      const checkboxId = nextGeneratedId("tdog-no");
      const row = createElement("div", { className: "tdog-engrave-row" });
      const checkbox = createElement("input", {
        className: CLASSES.noEngrave,
        attributes: {
          type: "checkbox",
          id: checkboxId
        }
      });
      const label = createElement("label", {
        text: "No engraving for this item",
        attributes: { for: checkboxId }
      });

      checkbox.addEventListener("change", () => {
        // Leave LPO's own fields exactly as they were before this feature
        // existed: empty. See setDeclineProperty for why. - TK 16/08/2026
        if (checkbox.checked) {
          clearGroupText(groupEl);

          // LPO can repopulate a field from its own state a beat after we
          // clear it. Sweep again, but only while the decline is still
          // ticked, so we never wipe text the customer has begun retyping.
          for (const delay of [250, 800]) {
            window.setTimeout(() => {
              if (checkbox.checked) clearGroupText(groupEl);
            }, delay);
          }
        }

        setDeclineProperty(groupEl, textInput, checkbox.checked);
        updateEngraveUI(wrapper);
      });

      /*
       * Delegated on the panel so it covers every field in the group,
       * including any LPO reveals later. - TK 23/08/2026
       */
      panel.addEventListener("input", (event) => {
        const field = event.target;
        if (!isElement(field) || !field.matches(SELECTORS.engraveTextInput)) return;
        if (!checkbox.checked) return;
        if ((field.value || "").trim().length === 0) return;

        // Customer changed their mind back. Untick, and clear the decline
        // property explicitly: assigning .checked fires no change event, so
        // without this the order would carry BOTH their engraving text and
        // "No Engraving Chosen".
        checkbox.checked = false;
        setDeclineProperty(groupEl, textInput, false);
        updateEngraveUI(wrapper);
      });

      row.append(checkbox, label);
      textInput.insertAdjacentElement("afterend", row);
    }

    function updateEngraveUI(wrapper) {
      if (!wrapper) return;

      const groups = getGroups(wrapper);
      if (groups.length < 2) return;

      ensureProgress(wrapper);

      for (const group of groups) {
        const collapse = getCollapse(group);
        if (!collapse) continue;

        setClassIfChanged(group, CLASSES.card, true);
        setClassIfChanged(collapse, CLASSES.collapse, true);
        setCollapseTitle(collapse, group);
        setBadge(collapse, getGroupState(group));
      }

      updateProgress(wrapper, groups);
      refreshGateFeedback(wrapper);
    }

    function bindDelegatedListeners(wrapper) {
      if (!wrapper || boundWrappers.has(wrapper)) {
        return;
      }

      boundWrappers.add(wrapper);

      const debouncedUpdate = debounce(
        () => updateEngraveUI(wrapper),
        CONFIG.engraveSyncDebounceMs
      );

      wrapper.addEventListener("input", debouncedUpdate, true);
      wrapper.addEventListener("change", debouncedUpdate, true);

      if (!engraveObserverByWrapper.has(wrapper)) {
        const observer = new MutationObserver((mutations) => {
          const relevant = mutations.some((mutation) =>
            Array.from(mutation.addedNodes).some((node) => isElement(node))
            || mutation.type !== "childList"
          );

          if (relevant) {
            debouncedUpdate();
          }
        });

        observer.observe(wrapper, {
          childList: true,
          subtree: true,
          attributes: true,
          characterData: false
        });

        engraveObserverByWrapper.set(wrapper, registerObserver(observer, wrapper));
      }
    }

    function enhance(root = document) {
      safeRun("EngraveUX.enhance", () => {
        const wrapper = getWrapperScoped(root);
        if (!wrapper) return;

        const groups = getGroups(wrapper);
        if (groups.length < 2) return;

        for (const group of groups) {
          if (!processedGroups.has(group)) {
            processedGroups.add(group);
            ensureNoEngraveRow(group, wrapper);
          }

          const collapse = getCollapse(group);
          if (collapse) {
            ensureBadge(collapse);
            setCollapseTitle(collapse, group);
          }
        }

        bindDelegatedListeners(wrapper);
        installGate();
        updateEngraveUI(wrapper);
      });
    }

    /************************************************************
     * Checkout gate - TK 16/08/2026
     *
     * Every engraving item on a bundle must be either filled in or explicitly
     * declined before the customer can add to cart. Our laser T&Cs say a blank
     * field is engraved with nothing, so the point of this is to make skipping
     * a deliberate, recorded act rather than something a customer can do by not
     * scrolling far enough.
     *
     * Scope is bundles only (GATE.minGroups). Single-engraving products keep
     * their existing behaviour.
     *
     * SAFETY: this gate fails OPEN. Every path that can throw is wrapped, and
     * any failure allows the purchase through. A bug in validation must never
     * be able to stop sales - the acceptable failure is someone slipping past,
     * never a customer who cannot buy.
     ************************************************************/
    const GATE = Object.freeze({
      minGroups: 2,
      alertClass: "tdog-engrave-alert",
      errorClass: "tdog-engrave-card--error"
    });

    let gateInstalled = false;

    // revealGroup() synthetically clicks the collapse header to open a panel.
    // That click re-enters our own document-level listener, which would block
    // again and reveal again, forever. This flag breaks the cycle. - TK 16/08/2026
    let gateRevealing = false;

    function unresolvedGroups(wrapper) {
      const groups = getGroups(wrapper);
      if (groups.length < GATE.minGroups) return [];

      return groups.filter((group) => {
        const state = getGroupState(group);
        return state !== "complete" && state !== "skipped";
      });
    }

    function wrapperForForm(formEl) {
      const formId = formEl?.getAttribute?.("id");

      // The LPO inputs sit outside the <form> and bind to it via form="...".
      // Walking back from one of them is the only reliable link between a
      // given product form and its engraving wrapper.
      if (formId) {
        const owned = document.querySelector(`${SELECTORS.engraveWrapper} [form="${cssEscape(formId)}"]`);
        const wrapper = owned?.closest(SELECTORS.engraveWrapper);
        if (wrapper) return wrapper;
      }

      return getWrapperScoped(document);
    }

    function isPanelOpen(groupEl) {
      const panel = getPanel(groupEl);
      if (!panel) return false;
      return panel.offsetParent !== null && panel.getBoundingClientRect().height > 0;
    }

    function revealGroup(groupEl) {
      safeRun("revealGroup", () => {
        gateRevealing = true;
        try {
          if (!isPanelOpen(groupEl)) {
            getCollapse(groupEl)?.click();
          }
        } finally {
          gateRevealing = false;
        }

        groupEl.scrollIntoView({ behavior: "smooth", block: "center" });

        window.setTimeout(() => {
          safeRun("revealGroup.focus", () => findTextInput(groupEl)?.focus({ preventScroll: true }));
        }, 400);
      });
    }

    function ensureAlert(wrapper) {
      const progress = ensureProgress(wrapper);

      let alert = progress.querySelector(`.${GATE.alertClass}`);
      if (alert) return alert;

      alert = createElement("div", {
        className: GATE.alertClass,
        attributes: { role: "alert", "aria-live": "assertive" }
      });
      alert.hidden = true;
      progress.appendChild(alert);

      return alert;
    }

    function paintUnresolved(wrapper, unresolved) {
      const flagged = new Set(unresolved);
      for (const group of getGroups(wrapper)) {
        setClassIfChanged(group, GATE.errorClass, flagged.has(group));
      }
    }

    function showGateMessage(wrapper, unresolved) {
      const alert = ensureAlert(wrapper);
      const names = unresolved.map(deriveItemName);

      const message = names.length === 1
        ? `Please finish the ${names[0]} engraving, or tick “No engraving for this item”.`
        : `Please finish these before adding to cart: ${names.join(", ")}. Or tick “No engraving for this item” on each one.`;

      setTextIfChanged(alert, message);
      if (alert.hidden) alert.hidden = false;
    }

    function clearGateMessage(wrapper) {
      const alert = wrapper.querySelector(`.${GATE.alertClass}`);
      if (alert && !alert.hidden) alert.hidden = true;

      for (const group of getGroups(wrapper)) {
        setClassIfChanged(group, GATE.errorClass, false);
      }
    }

    // Once the customer has been blocked, keep the errors live so they clear
    // themselves as each item is resolved, rather than only on the next click.
    function refreshGateFeedback(wrapper) {
      if (wrapper.dataset.tdogGateTried !== "1") return;

      const unresolved = unresolvedGroups(wrapper);
      if (unresolved.length === 0) {
        clearGateMessage(wrapper);
        return;
      }

      paintUnresolved(wrapper, unresolved);
      showGateMessage(wrapper, unresolved);
    }

    function resolveCartFormFromEvent(event) {
      const target = isElement(event.target) ? event.target : null;
      if (!target) return null;

      let form = null;

      if (event.type === "submit") {
        form = target.tagName === "FORM" ? target : null;
      } else {
        const submitter = target.closest(SELECTORS.formSubmitter);
        if (!submitter) return null;

        // A <button> with no type attribute defaults to submit; type="button" does not.
        const type = (submitter.getAttribute("type") || "submit").toLowerCase();
        if (submitter.tagName === "BUTTON" && type !== "submit") return null;

        const formAttr = submitter.getAttribute("form");
        form = formAttr ? document.getElementById(formAttr) : submitter.closest("form");
      }

      if (!form) return null;
      return (form.getAttribute("action") || "").includes("/cart/add") ? form : null;
    }

    function shouldBlock(event) {
      const form = resolveCartFormFromEvent(event);
      if (!form) return false;

      const wrapper = wrapperForForm(form);
      if (!wrapper) return false;

      const unresolved = unresolvedGroups(wrapper);
      if (unresolved.length === 0) {
        clearGateMessage(wrapper);
        return false;
      }

      wrapper.dataset.tdogGateTried = "1";
      paintUnresolved(wrapper, unresolved);
      showGateMessage(wrapper, unresolved);
      revealGroup(unresolved[0]);

      log(`Gate blocked add-to-cart: ${unresolved.length} unresolved engraving item(s)`,
        unresolved.map(deriveItemName));

      return true;
    }

    function onGateEvent(event) {
      if (gateRevealing) return;

      let blocked = false;

      try {
        blocked = shouldBlock(event);
      } catch (err) {
        // Fail open - never let a validation bug cost a sale.
        warn("Gate check failed; allowing purchase", err);
        return;
      }

      if (!blocked) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    }

    function installGate() {
      if (gateInstalled) return;
      gateInstalled = true;

      // Capture phase at document level so we run before the theme's own
      // product-form handler and before LPO. Both click (the button lives
      // outside the form and targets it via form="...") and submit (pressing
      // Enter inside an engraving field submits directly).
      document.addEventListener("click", onGateEvent, true);
      document.addEventListener("submit", onGateEvent, true);

      log("Engraving checkout gate installed");
    }

    return {
      enhance,
      updateEngraveUI,
      installGate
    };
  })();

  function enhanceEngravingBundleUX(root = document) {
    EngraveUX.enhance(root);
  }

  /************************************************************
   * Boot + dynamic hooks
   ************************************************************/
  function processStatic(root = document) {
    safeRun("processStatic", () => {
      ensureStyle();
      stylePersonaliseButtons(root);
      placeNewBadges(root);
      enhanceEngravingBundleUX(root);
    });
  }

  function processDynamicRoots(roots) {
    for (const root of roots) {
      processStatic(root);

      if (elementMatchesOrContains(root, SELECTORS.rapiRoot) || elementMatchesOrContains(root, SELECTORS.productFormWrapper)) {
        bindAllRapiSections(root);
      }
    }
  }

  async function boot() {
    processStatic(document);

    const globalObserver = new MutationObserver(
      debounce((mutations) => {
        processDynamicRoots(getRelevantRootsFromMutations(mutations));
      }, CONFIG.globalObserverDebounceMs)
    );

    globalObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    const scheduleStartupRescan = (delayMs) => {
      window.setTimeout(() => {
        log(`Startup rescan @ ${delayMs}ms`);
        processStatic(document);
        bindAllRapiSections(document);
      }, delayMs);
    };

    bindAllRapiSections(document);
    scheduleStartupRescan(250);
    scheduleStartupRescan(750);
    scheduleStartupRescan(1500);

    const onShopifySectionEvent = (event) => {
      const root = isElement(event.target) ? event.target : document;
      log("Shopify section event", event.type, root);

      // The section that just went away took its DOM with it; release any
      // observers still watching the detached nodes. - TK 16/08/2026
      pruneDetachedObservers();

      processStatic(root);
      bindAllRapiSections(root);
    };

    document.addEventListener("shopify:section:load", onShopifySectionEvent);
    document.addEventListener("shopify:section:select", onShopifySectionEvent);
    document.addEventListener("shopify:section:unload", pruneDetachedObservers);

    if (!shouldWatchForRapi()) {
      log("Skipped Rapi watch: no product form on this page. The global observer will still bind a bundle if one appears.");
      return;
    }

    const detected = await waitForRapi();
    if (!detected) {
      warn("Rapi not detected; bundle sync will continue through the global observer.");
      return;
    }

    bindAllRapiSections(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      boot().catch((err) => error("Boot failed", err));
    }, { once: true });
  } else {
    boot().catch((err) => error("Boot failed", err));
  }
})();